import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import DemoBanner from "@/components/DemoBanner";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import CourseDetail from "@/pages/CourseDetail";
import PreviewLesson from "@/pages/PreviewLesson";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import LessonPlayer from "@/pages/LessonPlayer";
import Quiz from "@/pages/Quiz";
import FinalTest from "@/pages/FinalTest";
import CertificatePage from "@/pages/CertificatePage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import LearningPath from "@/pages/LearningPath";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import StaticPage from "@/pages/StaticPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminCourseEdit from "@/pages/admin/AdminCourseEdit";
import AdminAIGenerator from "@/pages/admin/AdminAIGenerator";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminSales from "@/pages/admin/AdminSales";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminBundles from "@/pages/admin/AdminBundles";
import AdminDigest from "@/pages/admin/AdminDigest";
import AdminTutor from "@/pages/admin/AdminTutor";
import AdminDemo from "@/pages/admin/AdminDemo";
import BundleDetail from "@/pages/BundleDetail";
import Unsubscribe from "@/pages/Unsubscribe";

import TutorLanding from "@/pages/tutor/TutorLanding";
import TutorLayout from "@/pages/tutor/TutorLayout";
import TutorOnboarding from "@/pages/tutor/TutorOnboarding";
import TutorDashboard from "@/pages/tutor/TutorDashboard";
import TutorGenerate from "@/pages/tutor/TutorGenerate";
import TutorChat from "@/pages/tutor/TutorChat";
import TutorLanguage from "@/pages/tutor/TutorLanguage";
import TutorPricing from "@/pages/tutor/TutorPricing";
import TutorSuccess from "@/pages/tutor/TutorSuccess";
import TutorParent from "@/pages/tutor/TutorParent";
import TutorItem from "@/pages/tutor/TutorItem";

import PracticeLanding from "@/pages/practice/PracticeLanding";
import PracticeTestDetail from "@/pages/practice/PracticeTestDetail";
import PracticePlayer from "@/pages/practice/PracticePlayer";
import PracticeResults from "@/pages/practice/PracticeResults";
import PracticeDashboard from "@/pages/practice/PracticeDashboard";

import CentreLanding from "@/pages/centre/CentreLanding";
import CentreRegister from "@/pages/centre/CentreRegister";
import CentreTeacherApply from "@/pages/centre/CentreTeacherApply";
import TeacherDashboard from "@/pages/centre/TeacherDashboard";
import ParentDashboard from "@/pages/centre/ParentDashboard";
import CalgaryNw from "@/pages/centre/CalgaryNw";
import FreeAssessment from "@/pages/centre/FreeAssessment";
import ServicesPage from "@/pages/ServicesPage";
import AdminCentre from "@/pages/admin/AdminCentre";

import PartnersLanding from "@/pages/partners/PartnersLanding";
import PartnerApply from "@/pages/partners/PartnerApply";
import PartnerStorefront from "@/pages/partners/PartnerStorefront";
import PartnerLayout from "@/pages/partners/PartnerLayout";
import PartnerDashboard from "@/pages/partners/PartnerDashboard";
import PartnerCourses from "@/pages/partners/PartnerCourses";
import PartnerCourseEdit from "@/pages/partners/PartnerCourseEdit";
import PartnerOnboarding from "@/pages/partners/PartnerOnboarding";
import {
  PartnerStudentsPage, PartnerRegistrationsPage, PartnerAssessmentsPage,
  PartnerCertificatesPage, PartnerTrainersPage, PartnerMediaPage,
  PartnerAccreditationsPage, PartnerDocumentsPage, PartnerSalesPage,
  PartnerProfilePage, PartnerSupportPage,
} from "@/pages/partners/PartnerPortalPages";
import AdminPartners from "@/pages/admin/AdminPartners";
import VerifyCertificate from "@/pages/VerifyCertificate";

import "@/App.css";
import UpgradeModal from "@/components/UpgradeModal";

function Shell() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <DemoBanner />
      <UpgradeModal />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/courses" element={<Catalog />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/courses/:slug/preview/:lessonId" element={<PreviewLesson />} />
          <Route path="/bundles/:slug" element={<BundleDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/learning-path" element={<LearningPath />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/terms" element={<StaticPage pageKey="terms" />} />
          <Route path="/privacy" element={<StaticPage pageKey="privacy" />} />
          <Route path="/refund" element={<StaticPage pageKey="refund" />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/verify/:certId" element={<VerifyCertificate />} />

          {/* Free Practice Tests */}
          <Route path="/practice" element={<PracticeLanding />} />
          <Route path="/practice/dashboard" element={
            <ProtectedRoute><PracticeDashboard /></ProtectedRoute>
          } />
          <Route path="/practice/:slug" element={<PracticeTestDetail />} />
          <Route path="/practice/:slug/play/:attemptId" element={
            <ProtectedRoute><PracticePlayer /></ProtectedRoute>
          } />
          <Route path="/practice/:slug/results/:attemptId" element={
            <ProtectedRoute><PracticeResults /></ProtectedRoute>
          } />

          {/* Physical Learning Centre */}
          <Route path="/centre" element={<CentreLanding />} />
          <Route path="/calgary-nw" element={<CalgaryNw />} />
          <Route path="/free-assessment" element={<FreeAssessment />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/centre/register" element={<Navigate to="/register?role=parent" replace />} />
          <Route path="/centre/teacher-apply" element={<CentreTeacherApply />} />
          <Route path="/teacher/dashboard" element={
            <ProtectedRoute><TeacherDashboard /></ProtectedRoute>
          } />
          <Route path="/parent/dashboard" element={
            <ProtectedRoute><ParentDashboard /></ProtectedRoute>
          } />

          {/* Training Partner Portal */}
          <Route path="/partners" element={<PartnersLanding />} />
          <Route path="/partners/apply" element={<PartnerApply />} />
          <Route path="/partnerregis" element={<Navigate to="/register?role=partner" replace />} />
          <Route path="/partners/:slug" element={<PartnerStorefront />} />
          <Route path="/onboarding/partner" element={
            <ProtectedRoute><PartnerOnboarding /></ProtectedRoute>
          } />
          <Route path="/partner" element={
            <ProtectedRoute role="partner"><PartnerLayout /></ProtectedRoute>
          }>
            <Route index element={<PartnerDashboard />} />
            <Route path="dashboard" element={<PartnerDashboard />} />
            <Route path="courses" element={<PartnerCourses />} />
            <Route path="courses/new" element={<PartnerCourses />} />
            <Route path="courses/:id/edit" element={<PartnerCourseEdit />} />
            <Route path="cohorts" element={<PartnerDashboard />} />
            <Route path="students" element={<PartnerStudentsPage />} />
            <Route path="registrations" element={<PartnerRegistrationsPage />} />
            <Route path="assessments" element={<PartnerAssessmentsPage />} />
            <Route path="certificates" element={<PartnerCertificatesPage />} />
            <Route path="trainers" element={<PartnerTrainersPage />} />
            <Route path="media" element={<PartnerMediaPage />} />
            <Route path="accreditations" element={<PartnerAccreditationsPage />} />
            <Route path="documents" element={<PartnerDocumentsPage />} />
            <Route path="sales" element={<PartnerSalesPage />} />
            <Route path="profile" element={<PartnerProfilePage />} />
            <Route path="support" element={<PartnerSupportPage />} />
          </Route>

          {/* Tutoring / Teacher4Me */}
          <Route path="/tutor-landing" element={<TutorLanding />} />
          <Route path="/tutor/onboarding" element={
            <ProtectedRoute><TutorOnboarding /></ProtectedRoute>
          } />
          <Route path="/tutor/subscription/success" element={
            <ProtectedRoute><TutorSuccess /></ProtectedRoute>
          } />
          <Route path="/tutor" element={
            <ProtectedRoute><TutorLayout /></ProtectedRoute>
          }>
            <Route index element={<TutorDashboard />} />
            <Route path="worksheet" element={<TutorGenerate />} />
            <Route path="quiz" element={<TutorGenerate />} />
            <Route path="test" element={<TutorGenerate />} />
            <Route path="study-plan" element={<TutorGenerate />} />
            <Route path="homework" element={<TutorChat mode="homework" />} />
            <Route path="chat" element={<TutorChat mode="chat" />} />
            <Route path="english" element={<TutorLanguage />} />
            <Route path="french" element={<TutorLanguage />} />
            <Route path="pricing" element={<TutorPricing />} />
            <Route path="parent" element={<TutorParent />} />
            <Route path="items/:id" element={<TutorItem />} />
          </Route>

          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/learn/:courseId" element={
            <ProtectedRoute><LessonPlayer /></ProtectedRoute>
          } />
          <Route path="/quiz/:courseId/:moduleId" element={
            <ProtectedRoute><Quiz /></ProtectedRoute>
          } />
          <Route path="/final-test/:courseId" element={
            <ProtectedRoute><FinalTest /></ProtectedRoute>
          } />
          <Route path="/certificate/:certId" element={
            <ProtectedRoute><CertificatePage /></ProtectedRoute>
          } />
          <Route path="/payment/success" element={
            <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>
          }>
            <Route index element={<AdminOverview />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses/:courseId" element={<AdminCourseEdit />} />
            <Route path="ai-generator" element={<AdminAIGenerator />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="sales" element={<AdminSales />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="bundles" element={<AdminBundles />} />
            <Route path="digest" element={<AdminDigest />} />
            <Route path="tutor" element={<AdminTutor />} />
            <Route path="demo" element={<AdminDemo />} />
            <Route path="centre" element={<AdminCentre />} />
            <Route path="partners" element={<AdminPartners />} />
          </Route>
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}
